import { ReactNode } from 'react';
import { Position } from './types';

export const formatCurrency = (value: number) =>
  `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const formatSignedCurrency = (value: number) =>
  `${value >= 0 ? '+' : ''}${formatCurrency(value)}`;

export const formatPercent = (value: number) =>
  `${value >= 0 ? '+' : ''}${(value * 100).toFixed(2)}%`;

/** Difference between two rates — same numeric scale, p.p. unit instead of %. */
export const formatPercentagePoints = (value: number) =>
  `${value >= 0 ? '+' : ''}${(value * 100).toFixed(2)} p.p.`;

export const formatSplitPercent = (value: number) =>
  `${(value * 100).toFixed(2)}%`;

export const formatMaybeCurrency = (value?: number) =>
  value === undefined || value === null ? '—' : formatCurrency(value);

export const formatMaybeNumber = (value?: number) =>
  value === undefined || value === null ? '—' : value.toLocaleString();

export const formatMaybeDateTime = (value?: string) =>
  value ? new Date(value).toLocaleString() : '—';

export interface PerformanceData {
  today?: { amount: number; percent: number; alfa?: number; benchmarkReturn?: number };
  lastMonth?: { amount: number; percent: number; alfa?: number; benchmarkReturn?: number };
  sinceStart?: { amount: number; percent: number; alfa?: number; benchmarkReturn?: number };
}

// Token-class recipes translated from accounts.css (shared by both frontends).
const SURFACE_BOX = 'rounded-[10px] border border-[color:var(--nq-line-slate)] bg-muted';
const META_LABEL =
  'mb-1 text-xs font-semibold uppercase tracking-[0.5px] text-muted-foreground';
const PERF_SUB = 'ml-1 text-xs font-semibold';
const POS_TH =
  'sticky top-0 z-[1] border-b border-[color:var(--nq-line-slate)] bg-muted px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground';
const POS_TD = 'border-t border-[rgba(255,255,255,0.06)] px-3 py-2.5 align-middle';
const POS_EMPTY = 'mt-2.5 text-[13px] text-muted-foreground';
const ERR_MSG =
  'rounded-[8px] border border-destructive/40 bg-destructive/10 p-3 text-center text-[14px] text-destructive';

export interface SharedAccountBodyProps {
  totalValue?: number;
  cash?: number;
  invested?: number;
  performance?: PerformanceData;
  /** NYSE regular session open. When explicitly false, the "Gain today" box
   * shows a "(market closed)" note since its value is the frozen last session. */
  marketOpen?: boolean;
  positions?: Position[];
  positionsLoaded: boolean;
  positionsLoading: boolean;
  positionsError?: string | null;
  onLoadPositionsIfNeeded: () => void;
  details: ReactNode;
  /** Optional row rendered between the summary strip and the Positions /
   * Details disclosures (e.g. per-account admin controls). */
  controls?: ReactNode;
}

const SharedAccountBody = ({
  totalValue,
  cash,
  invested,
  performance,
  marketOpen,
  positions = [],
  positionsLoaded,
  positionsLoading,
  positionsError,
  onLoadPositionsIfNeeded,
  details,
  controls,
}: SharedAccountBodyProps) => {
  const sinceStartPct = performance?.sinceStart?.percent;
  const cashPct =
    totalValue && cash !== undefined && totalValue > 0
      ? cash / totalValue
      : undefined;
  const investedPct =
    totalValue && invested !== undefined && totalValue > 0
      ? invested / totalValue
      : undefined;

  const summaryClass =
    'cursor-pointer select-none font-semibold text-foreground hover:underline';

  return (
    <>
      <div className={`mb-3 p-4 ${SURFACE_BOX}`}>
        <div className={META_LABEL}>Portfolio value</div>
        <div className="font-display text-[34px] font-bold leading-[1.1] text-foreground">
          {totalValue !== undefined ? (
            <>
              {formatCurrency(totalValue)}
              {sinceStartPct !== undefined && (
                <span
                  className={`ml-1 align-middle text-[0.55em] font-semibold ${
                    sinceStartPct >= 0 ? 'text-positive' : 'text-negative'
                  }`}
                >
                  {' '}
                  ({formatPercent(sinceStartPct)})
                </span>
              )}
            </>
          ) : (
            <em>N/A</em>
          )}
        </div>
      </div>

      <div className="mb-2 grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2">
        <div className={`px-3 py-2.5 ${SURFACE_BOX}`}>
          <div className={META_LABEL}>Gain today</div>
          {performance?.today ? (
            <div className={performance.today.percent >= 0 ? 'text-positive' : 'text-negative'}>
              {formatSignedCurrency(performance.today.amount)}{' '}
              <span className={PERF_SUB}>({formatPercent(performance.today.percent)})</span>
            </div>
          ) : (
            <em>N/A</em>
          )}
          {marketOpen === false && (
            <div className="mt-1 text-[10px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
              (market closed)
            </div>
          )}
        </div>
        <div className={`px-3 py-2.5 ${SURFACE_BOX}`}>
          <div className={META_LABEL}>Gain last 30d</div>
          {performance?.lastMonth ? (
            <div
              className={performance.lastMonth.percent >= 0 ? 'text-positive' : 'text-negative'}
            >
              {formatSignedCurrency(performance.lastMonth.amount)}{' '}
              <span className={PERF_SUB}>({formatPercent(performance.lastMonth.percent)})</span>
            </div>
          ) : (
            <em>N/A</em>
          )}
        </div>
        <div className={`px-3 py-2.5 ${SURFACE_BOX}`}>
          <div className={META_LABEL}>Gain since start</div>
          {performance?.sinceStart ? (
            <div
              className={performance.sinceStart.percent >= 0 ? 'text-positive' : 'text-negative'}
            >
              {formatSignedCurrency(performance.sinceStart.amount)}{' '}
              <span className={PERF_SUB}>({formatPercent(performance.sinceStart.percent)})</span>
              {performance.sinceStart.alfa !== undefined && (
                <div className="mt-1.5 text-xs font-semibold text-secondary">
                  Alfa vs SP500: {formatPercent(performance.sinceStart.alfa)}
                </div>
              )}
            </div>
          ) : (
            <em>N/A</em>
          )}
        </div>
      </div>

      <div
        className={`mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-2 px-3 py-2.5 text-sm font-semibold text-[color:var(--text-secondary)] ${SURFACE_BOX}`}
      >
        <span>
          <strong className="mr-1 text-xs font-semibold uppercase tracking-[0.5px] text-muted-foreground">
            Invested
          </strong>{' '}
          {invested !== undefined ? (
            <>
              {formatCurrency(invested)}
              {investedPct !== undefined && (
                <span className="font-medium text-muted-foreground">
                  {' '}
                  ({formatSplitPercent(investedPct)})
                </span>
              )}
            </>
          ) : (
            <em>N/A</em>
          )}
        </span>
        <span className="select-none font-normal text-muted-foreground" aria-hidden="true">
          ·
        </span>
        <span>
          <strong className="mr-1 text-xs font-semibold uppercase tracking-[0.5px] text-muted-foreground">
            Cash
          </strong>{' '}
          {cash !== undefined ? (
            <>
              {formatCurrency(cash)}
              {cashPct !== undefined && (
                <span className="font-medium text-muted-foreground">
                  {' '}
                  ({formatSplitPercent(cashPct)})
                </span>
              )}
            </>
          ) : (
            <em>N/A</em>
          )}
        </span>
      </div>

      {controls && <div className="mb-4">{controls}</div>}

      <details
        className="mt-2.5"
        onToggle={(e) => {
          const el = e.currentTarget as HTMLDetailsElement;
          if (!el.open) return;
          onLoadPositionsIfNeeded();
        }}
      >
        <summary className={summaryClass}>Positions</summary>
        <div className="mt-2.5">
          {positionsError && <div className={`mt-2.5 ${ERR_MSG}`}>{positionsError}</div>}

          {positionsLoading ? (
            <p className={POS_EMPTY}>Loading positions…</p>
          ) : positionsLoaded ? (
            positions.length === 0 ? (
              <p className={POS_EMPTY}>No open positions.</p>
            ) : (
              <div className="mt-2.5 overflow-x-auto rounded-[10px] border border-[color:var(--nq-line-slate)]">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr>
                      <th className={`${POS_TH} font-semibold`}>Symbol</th>
                      <th className={`${POS_TH} text-right text-[rgba(255,255,255,0.42)]`}>
                        Avg entry
                      </th>
                      <th className={`${POS_TH} text-right text-[rgba(255,255,255,0.42)]`}>
                        Current
                      </th>
                      <th className={`${POS_TH} text-right`}>Market value</th>
                      <th className={`${POS_TH} text-right`}>Unrealized P/L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map((p) => {
                      const pl = p.unrealizedPL;
                      const plPct = p.unrealizedPLPct;
                      const plClass =
                        plPct !== undefined
                          ? plPct >= 0
                            ? 'text-positive'
                            : 'text-negative'
                          : pl === undefined
                            ? undefined
                            : pl >= 0
                              ? 'text-positive'
                              : 'text-negative';
                      return (
                        <tr
                          key={p.symbol}
                          className="transition-[background] duration-150 hover:bg-[rgba(255,255,255,0.04)]"
                        >
                          <td className={`${POS_TD} font-semibold`}>
                            <strong>{p.symbol}</strong>
                          </td>
                          <td className={`${POS_TD} text-right tabular-nums text-muted-foreground`}>
                            {formatMaybeCurrency(p.avgEntryPrice)}
                          </td>
                          <td className={`${POS_TD} text-right tabular-nums text-muted-foreground`}>
                            {formatMaybeCurrency(p.currentPrice)}
                          </td>
                          <td className={`${POS_TD} text-right tabular-nums`}>
                            {formatMaybeCurrency(p.marketValue)}
                          </td>
                          <td className={`${POS_TD} text-right tabular-nums ${plClass ?? ''}`}>
                            {pl === undefined
                              ? '—'
                              : `${pl >= 0 ? '+' : ''}${formatCurrency(pl)}`}
                            {p.unrealizedPLPct !== undefined && (
                              <span className={PERF_SUB}> ({formatPercent(p.unrealizedPLPct)})</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <p className={POS_EMPTY}>Expanding will load positions for this account.</p>
          )}
        </div>
      </details>

      <details className="mt-2.5">
        <summary className={summaryClass}>Details</summary>
        <div className="mt-3 border-t border-[color:var(--nq-line-slate)] pt-3">{details}</div>
      </details>
    </>
  );
};

export default SharedAccountBody;
